// R1b-β Step 4 Deliverable A — Platform Billing surface.
//
// Everything here is either (a) live entitlement plumbing, or (b) an
// honest phase_n placeholder. There are no fake subscription rows, no
// fake invoices, no fake payment methods. The whole point of this page
// is to make ERF Founder's paywall immunity VISIBLE — the immune banner
// renders whenever `entitlements.is_paywall_immune()` returns true.
import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { ShieldCheck, CreditCard, Receipt, Lock, Info } from "lucide-react";
import StatusChip from "../../components/StatusChip";

const FEATURE_KEYS = ["projects", "estimates", "invoicing", "advanced_reporting", "multi_state_licensing"];

function PhaseCard({ testId, title, subtitle, icon: Icon, phase = 5 }) {
  return (
    <Card className="border-erf-borderCream bg-erf-cream" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-sm bg-erf-gold/10 border border-erf-gold/40 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-erf-goldHover" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">
                Phase {phase}
              </div>
              <div className="font-display font-semibold text-erf-dark mt-0.5">{title}</div>
              <p className="text-xs font-nav text-erf-dark/70 mt-1 leading-relaxed">{subtitle}</p>
            </div>
          </div>
          <StatusChip status="phase_n" label={`Phase ${phase}`} />
        </div>
        <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-erf-dark/50">
          <Lock className="w-3 h-3" /> Truthful placeholder — no fake data
        </div>
      </CardContent>
    </Card>
  );
}

export default function BillingAdmin() {
  const { user } = useAuth();
  const [immune, setImmune] = useState(null);
  const [immuneReason, setImmuneReason] = useState(null);
  const [checks, setChecks] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/entitlements");
        if (cancelled) return;
        const byKey = Object.fromEntries((data.checks || []).map((c) => [c.feature_key, c]));
        setChecks(FEATURE_KEYS.map((k) => byKey[k] || { feature_key: k, error: "not returned" }));
        setImmune(Boolean(data.paywall_immune));
        if (data.paywall_immune) setImmuneReason("paywall_immune_flag");
      } catch (e) {
        setErr(e?.response?.data?.detail?.message || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AdminAppShell
      activeNav="people-access"
      title="Platform Billing"
      subtitle="Your ERF ConstructionOS subscription and platform invoices"
      breadcrumbs={[
        { label: "Settings", to: "/admin/settings" },
        { label: "Platform Billing", to: null },
      ]}
      user={user}
    >
      <div
        className="mx-auto w-full max-w-[1600px] box-border px-4 md:px-6 lg:px-8 space-y-6"
        data-testid="admin-billing-page"
      >
        {/* Section 1 — Current Plan */}
        {immune === true && (
          <Card
            className="border-erf-gold/70 bg-erf-cream"
            data-testid="admin-billing-paywall-immune-banner"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-sm bg-erf-gold/15 border border-erf-gold flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-erf-goldHover" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Founding Tenant</div>
                    <div className="font-display font-semibold text-erf-dark text-lg mt-0.5">
                      Paywall Immune — this tenant is exempt from platform billing
                    </div>
                    <p className="text-xs font-nav text-erf-dark/70 mt-1 leading-relaxed">
                      Founding tenant / operator-configured immunity ({immuneReason || "paywall_immune"}).
                      No subscription meter or platform invoice will ever be raised against this tenant.
                    </p>
                  </div>
                </div>
                <StatusChip status="connected" label="Immune" />
              </div>
            </CardContent>
          </Card>
        )}
        {immune === false && (
          <PhaseCard
            testId="admin-billing-plan-phasen"
            title="Current Plan"
            subtitle="No subscription plan is on file. Subscription billing is not implemented."
            icon={CreditCard}
          />
        )}

        {/* Sections 2 + 3 — honest phase_n */}
        <div className="grid gap-4 md:grid-cols-2">
          <PhaseCard
            testId="admin-billing-invoices-phasen"
            title="Platform Invoices"
            subtitle="Platform invoice history will appear here once billing is wired."
            icon={Receipt}
          />
          <PhaseCard
            testId="admin-billing-payment-phasen"
            title="Payment Method"
            subtitle="Stripe subscription payment method setup will appear here once billing is wired."
            icon={CreditCard}
          />
        </div>

        {/* Section 4 — Entitlement Status (LIVE) */}
        <Card className="border-erf-borderCream bg-white" data-testid="admin-billing-entitlement-status">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Live entitlement plumbing</div>
                <div className="font-display font-semibold text-erf-dark text-lg mt-0.5">Entitlement Status</div>
                <p className="text-xs font-nav text-erf-dark/70 mt-1">
                  Every feature key resolves through <span className="font-mono">entitlements.check_entitlement()</span>.
                  Reasons are honest — <span className="font-mono">paywall_immune</span> means this tenant is exempt;
                  <span className="font-mono"> phase_n_billing_not_yet_gated</span> means no live paywall is enforced yet.
                </p>
              </div>
              <StatusChip status="connected" label="Live" />
            </div>
            {err && (
              <div className="text-xs font-mono text-red-700 py-2" data-testid="admin-billing-entitlement-error">
                {err}
              </div>
            )}
            <div className="grid grid-cols-3 text-[11px] font-mono uppercase tracking-widest text-erf-dark/50 border-b border-erf-borderCream pb-2">
              <div>Paywall status</div>
              <div>Reason</div>
              <div className="text-right">Value</div>
            </div>
            <div className="grid grid-cols-3 py-2 text-xs font-mono border-b border-erf-borderCream/60"
                 data-testid="admin-billing-entitlement-paywall-row">
              <div className="text-erf-dark">Paywall status</div>
              <div className="text-erf-dark/70">
                {immune === true ? "immune (paywall_immune)" : immune === false ? "standard (not immune)" : "…"}
              </div>
              <div className="text-right">
                <StatusChip
                  status={immune === true ? "connected" : "phase_n"}
                  label={immune === true ? "Immune" : "Standard"}
                />
              </div>
            </div>
            <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 mb-1">
              Feature checks · check_entitlement(tenant_id, feature_key)
            </div>
            <div className="grid grid-cols-3 text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 border-b border-erf-borderCream pb-2">
              <div>Feature key</div>
              <div>Allowed</div>
              <div>Reason</div>
            </div>
            {(checks || []).map((c) => (
              <div key={c.feature_key} className="grid grid-cols-3 py-2 text-xs font-mono border-b border-erf-borderCream/40"
                   data-testid={`admin-billing-entitlement-row-${c.feature_key}`}>
                <div className="text-erf-dark">{c.feature_key}</div>
                <div className={c.allowed ? "text-emerald-700" : "text-red-700"}>
                  {c.error ? "—" : c.allowed ? "true" : "false"}
                </div>
                <div className="text-erf-dark/70">{c.error || c.reason || "—"}</div>
              </div>
            ))}
            {!checks && !err && (
              <div className="text-xs font-mono text-erf-dark/50 py-3">Loading feature checks…</div>
            )}
          </CardContent>
        </Card>

        <div className="text-[11px] font-mono uppercase tracking-widest text-erf-dark/40 flex items-center gap-1.5">
          <Info className="w-3 h-3" /> Truthful billing surface · no fake subscription rows · no fake invoices
        </div>
      </div>
    </AdminAppShell>
  );
}
